import { Edit, Person, Warning } from '@mui/icons-material'
import { Box, Grid, IconButton, styled, Tooltip, Typography, useTheme } from '@mui/material'
import { Context, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCertificateCache, useGetCertificates } from 'src/api/Certificates'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateMooringCache } from 'src/api/moorings'
import { invalidatePositionCache } from 'src/api/positions'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateScheduledTaskCache } from 'src/api/scheduledTask'
import { useGenerateSystemReports } from 'src/api/system/system-report'
import { ReactComponent as LocalityIcon } from 'src/assets/icons/localityIcon.svg'
import { ReactComponent as ReleaseIcon } from 'src/assets/icons/releaseIcon.svg'
import { ReactComponent as SendToServiceStationIcon } from 'src/assets/icons/serviceStationIcon.svg'
import { ReactComponent as DocumentDownload } from 'src/assets/svg/documentDownload.svg'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { FollowButton } from 'src/components/notifications/FollowButton'
import { ICertificate } from 'src/generated-types/Certificate'
import { IMainComponent } from 'src/generated-types/main-component'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { downloadFileLocal } from 'src/utils/downloadFile'
import { invalidateLocalityCache, useGetLocality } from '../../../api/localities'
import {
  invalidateMainComponentCache,
  useGetMainComponentById,
  useGetMainComponentROVVideos,
  useReleaseMainComponent,
} from '../../../api/main-component'
import { AuthorizationProvider, hasPermission } from '../../../components/common/Authorization'
import { CenteredSpinner, Spinner } from '../../../components/Spinner'
import {
  CertificationValidationPeriod,
  IMainComponentType,
  MainComponentStatus,
  MovementType,
  SystemReportTypes,
} from '../../../enums'
import { AuthorizationType } from '../../../enums/auth'
import { permissionValue } from '../../../enums/permissionValue'
import { useDialogCtx } from '../../../hooks/context-hooks'
import { useSendToLocalityDialogSubmit } from '../../../hooks/useSendToLocalityDialogSubmit'
import { UpdateMainComponentDialog } from '../../customer/mainComponents/UpdateMainComponentDialog'
import { SendToLocalityDialog } from '../../serviceStations/SendToLocalityDialog'
import ChangeLocationDialog from '../Equipment/ChangeLocationDialog'
import { BulkUploadRovDialog } from '../positions/components/BulkUploadRovDialog'
import ChangeEquipmentOwnerModal from '../positions/components/ChangeEquipmentOwnerModal'
import { MainComponentSendModalComponent } from '../positions/components/MainComponentSendModal'
import { EquipmentHeaderSection } from './components/EquipmentHeaderSection'
import DeviationSection from './detailModal/DeviationSection'
import DocumentsAndEventsSection from './detailModal/DocumentsAndEventsSection'
import HistoryAndMaintenanceSection from './detailModal/HistoryAndMaintenanceSection'
import ROVSection from './detailModal/ROVSection'
import WashingReportAndImpregnationSection from './detailModal/WashingReportAndImpregnationSection'
// import { InteractiveSchemaReportWizard } from '../../../components/interactive-schema/InteractiveSchemaReportWizard'
import { invalidateDeviation } from 'src/api/deviation'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'

export interface IEquipmentDetailModalProps {
  context?: Context<any> | null
  permission?: number
  customerId?: EntityId
  localityId?: EntityId
  mainComponentId?: any
  mainComponentTypeId?: EntityId
  serviceStationId?: EntityId
  adminCustomerId?: any
  pop?: any
  storageId?: EntityId
  disabledAddButton?: boolean
  isCustomerGlobalServiceStationProvider?: boolean
  serviceStationOwnerId?: EntityId
  isReadOnly?: boolean
  viewingLocalityId?: EntityId
  shouldCloseMainDialog?: boolean
}

export const EquipmentDetailModal: FC<IEquipmentDetailModalProps> = ({
  context,
  permission,
  customerId: cId,
  localityId,
  serviceStationId,
  storageId,
  adminCustomerId,
  mainComponentId,
  mainComponentTypeId,
  pop = () => {},
  disabledAddButton = false,
  isCustomerGlobalServiceStationProvider,
  serviceStationOwnerId,
  isReadOnly = false,
  viewingLocalityId,
  shouldCloseMainDialog = false,
}) => {
  const { t } = useTranslation(['main-component', 'common', 'equipment', 'activity-logs', 'overall-reports', 'rov'])
  const customerId = useMemo(() => cId ?? adminCustomerId, [cId, adminCustomerId])
  const { data, isLoading, error } = useGetMainComponentById(customerId as EntityId, mainComponentId as EntityId, {
    includeServiceStation: !!serviceStationId,
    includeStorage: !!storageId,
  })
  const { mutateAsync } = useReleaseMainComponent(customerId as EntityId)
  const { mutateAsync: generateAsync, isLoading: isGenerating } = useGenerateSystemReports()
  const { data: locality, isLoading: isLoadingLocality } = useGetLocality(
    customerId as EntityId,
    data?.selectedLocalityId ? (data?.selectedLocalityId as EntityId) : (0 as EntityId),
    { mainComponentId },
  )
  const { data: rovVideosWithSharedCategories } = useGetMainComponentROVVideos(
    customerId as EntityId,
    mainComponentId as EntityId,
  )
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)
  const { data: releaseSchemas, isLoading: isLoadingReleaseSchemas } = useGetFormSchemasForMovement(
    customerId as EntityId,
    { equipmentTypeIds: [mainComponentTypeId as number], movementTypes: [MovementType.Release] },
  )
  const { onSubmit: submitSendToLocality, prepareEquipmentId } = useSendToLocalityDialogSubmit({
    customerId: customerId as EntityId,
    fallbackLocalityId: localityId,
    onSuccess: () => {
      invalidateMainComponentCache.getMainComponentById(customerId as EntityId, mainComponentId as EntityId)
      invalidateDeviation.getDeviations(customerId)
    },
  })
  const theme = useTheme()
  const { localityId: localityIdFromURL } = useParams()
  const [selectedCategoryId, setSelectedCategoryId] = useState<EntityId | null>(null)

  const { data: certificates } = useGetCertificates(mainComponentId as EntityId, {})

  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false)

  useEffect(() => {
    setIsReadOnlyMode(isReadOnly)

    // If the equipment is not in the locality that tries to view the equipment, read only mode is applied
    if (data) {
      const isOutOfLocality =
        viewingLocalityId != null &&
        ((data.status != MainComponentStatus.Outgoing &&
          data.componentServiceStations &&
          data.componentServiceStations.some(s => s.isActive)) ||
          (data.storedEquipment && data.storedEquipment.length > 0) ||
          viewingLocalityId != data.selectedLocalityId)
      setIsReadOnlyMode(isReadOnly || isOutOfLocality)
    }
  }, [isReadOnly, data, viewingLocalityId])

  const onUpdate = useCallback(
    async id => {
      const added = await showDialog(
        UpdateMainComponentDialog,
        {
          componentProps: {
            id: id as EntityId,
            mainComponentTypeName: data?.type.name as string,
            customerId: customerId as EntityId,
            localityId: localityId,
            serviceStationId: serviceStationId,
            isCustomerSelectionEnable: isCustomerGlobalServiceStationProvider,
            isUpdateCustomerEnable: !data?.hasSentToServiceStation,
            serviceStationOwnerId: isCustomerGlobalServiceStationProvider ? serviceStationOwnerId : undefined,
          },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [data?.mainComponentTypeName, serviceStationId, data?.customerId],
  )

  const onAddRovVideo = async () => {
    const added = await showDialog(
      BulkUploadRovDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mainComponentId: mainComponentId as EntityId,
          equipmentOnly: !localityId || !!(serviceStationId || storageId),
          rovVideosWithSharedCategories: rovVideosWithSharedCategories,
          selectedCategoryId: selectedCategoryId,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )

    if (!added) return
  }

  const showChangeLocationDialog = useCallback(
    async (mainComponent: IMainComponent, localityId?: EntityId) => {
      await showDialog(
        ChangeLocationDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId,
            mainComponent: mainComponent,
            closeMainDialog: () => {
              pop()
            },
          },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
    },
    [customerId, localityId, locality?.id],
  )

  const onSendToLocality = useCallback(() => {
    prepareEquipmentId([mainComponentId] as EntityId[])
    showDialog(
      SendToLocalityDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          mainComponentId: mainComponentId as EntityId,
          mainComponentTypeId: data?.typeId as EntityId,
          onSubmit: submitSendToLocality,
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }, [customerId, mainComponentId, prepareEquipmentId, showDialog, submitSendToLocality])

  const onSend = async () => {
    const updated = await showDialog(MainComponentSendModalComponent, {
      title: t('sendTo', { ns: 'main-component' }),
      componentProps: {
        customerId: customerId as EntityId,
        localityId: locality?.id as EntityId,
        mooringId: data?.position?.mooringId as EntityId,
        positionId: data?.positionId as EntityId,
        id: mainComponentId as EntityId,
        localityIdFromURL: localityIdFromURL,
        onlyServiceStation: false,
        onCloseMainDialog: shouldCloseMainDialog ? () => pop() : undefined,
      },
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    })
    if (!updated) return
  }

  const performRelease = useCallback(
    async (reports: IFormSchemaReport[]) => {
      await mutateAsync(
        { id: data?.id as number, localityId: localityId as EntityId, formSchemaReports: reports } as any,
        {
          onSuccess: () => {
            toast.success(t('main-component-released-from-cage-toast', { ns: 'main-component' }))
            invalidateMainComponentCache.getMainComponents(
              customerId as EntityId,
              localityId as EntityId,
              data?.position?.mooringId as EntityId,
              data?.positionId as EntityId,
            )
            const typeId = data?.typeId ?? data?.type?.id
            if (typeId != null) {
              invalidateMainComponentCache.getMainComponentsByComponentType(customerId as EntityId, typeId as EntityId)
            }
            invalidationActivityLogCache.getCageActivityLogs()
            invalidatePositionCache.getCagePositionDeviations(
              customerId as EntityId,
              localityId as EntityId,
              data?.positionId as EntityId,
            )
            invalidateLocalityCache.getLocalityWithActivityCounts(customerId as EntityId, localityId as EntityId)
            invalidateLocalityCache.getLocality(customerId as EntityId, localityId as EntityId)
            invalidateMainComponentCache.getMainComponentById(customerId as EntityId, mainComponentId as EntityId)
            invalidateMainComponentCache.getEquipment(customerId)
            invalidateMooringCache.getMooringsForMap(customerId, localityId)
            invalidateMooringCache.getGetMooring(customerId, localityId, data?.position?.mooringId as EntityId)
            invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId as EntityId)
            invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId as EntityId)
            invalidateCertificateCache.getCertificates(mainComponentId as EntityId)
            invalidateLocalityCache.getCageStatus(customerId as EntityId, localityId as EntityId)
            invalidateScheduledTaskCache.useEquipmentOfScheduledWorkOrder(localityId as EntityId)
            invalidateDeviation.getDeviations(customerId)
            pop()
          },
        },
      )
    },
    [data, mutateAsync, localityId, customerId],
  )

  const onRelease = useCallback(async () => {
    if (!data) return
    showConfirmDialog(
      t('release-from-cage'),
      t('release-from-cage-description'),
      {
        acceptText: t('yes'),
        cancelText: t('no'),
      },
      async confirmPop => {
        confirmPop()

        await showChecklistsDialog({
          schemas: releaseSchemas,
          isLoadingSchemas: isLoadingReleaseSchemas,
          confirmText: t('release-from-cage'),
          mainComponentId: mainComponentId as EntityId,
          onConfirm: performRelease,
        })
      },
    )
  }, [
    data,
    performRelease,
    releaseSchemas,
    showChecklistsDialog,
    customerId,
    isLoadingReleaseSchemas,
    mainComponentId,
    t,
  ])

  const onChangeEquipmentOwner = useCallback(() => {
    showDialog(
      ChangeEquipmentOwnerModal,
      {
        opts: { maxWidth: 'sm', fullWidth: true },
        componentProps: {
          previousOwnerId: data?.customerId as EntityId,
          equipmentId: mainComponentId as EntityId,
          parentSupplierId: data?.supplier?.parentSupplierId as EntityId,
          status: data?.status as number,
          serviceStationId: serviceStationId,
          serviceStationOwnerId: serviceStationOwnerId,
          equipmentTypeId: data?.typeId as EntityId,
          currentSupplier: data?.supplier,
        },
      },
      undefined,
      true,
    )
  }, [customerId, showDialog, data?.customerId, mainComponentId, data, serviceStationId, serviceStationOwnerId])

  const certificate = useMemo(() => {
    const validCertificateList =
      data?.certificates &&
      (data?.certificates ?? [])
        .filter(c => c.isValid)
        .sort((a, b) => new Date(b.validUntil).getTime() - new Date(a.validUntil).getTime())
    if (validCertificateList && validCertificateList.length > 0) return validCertificateList[0]
  }, [data?.certificates])

  const certificateValidationTime = useMemo(() => {
    if (certificate?.expiresIn == null || certificate?.numberOfDays == null) return null
    if (certificate?.expiresIn && certificate?.numberOfDays) {
      return (certificate?.expiresIn * 100) / certificate?.numberOfDays
    }
    return 0
  }, [certificate])

  const status: CertificationValidationPeriod = useMemo(() => {
    if (certificateValidationTime != undefined && certificateValidationTime >= 0) {
      if (certificateValidationTime < CertificationValidationPeriod.LOW) {
        return CertificationValidationPeriod.LOW
      } else if (certificateValidationTime < CertificationValidationPeriod.MEDIUM) {
        return CertificationValidationPeriod.MEDIUM
      } else if (certificateValidationTime <= CertificationValidationPeriod.HIGH) {
        return CertificationValidationPeriod.HIGH
      }
    }
    return CertificationValidationPeriod.NA
  }, [certificateValidationTime])

  // TODO: Activity templates feature is unavailable at the moment

  // const handleStartActivity = useCallback(async () => {
  //   await showDialog(
  //     InteractiveSchemaReportWizard,
  //     {
  //       title: t('startActivity', { ns: 'interactive-schema' }),
  //       componentProps: {
  //         equipmentId: mainComponentId,
  //         equipmentTypeId: data?.type?.id,
  //         customerId,
  //         equipmentName: `${getTranslatedText(data?.type?.name ?? '')} / ${data?.serialNumber}`,
  //         locationName: locality?.name,
  //         cageNumber: data?.position ? getCageName(data?.position?.name, data?.position?.reference) : undefined,
  //       },
  //       opts: {
  //         maxWidth: 'lg',
  //         fullWidth: true,
  //       },
  //       isAutoCloseDisabled: true,
  //     },
  //     undefined,
  //     true,
  //   )
  // }, [showDialog, mainComponentId, data, customerId, locality, t])

  if (isLoading) return <CenteredSpinner />

  if (error || data === undefined) {
    return (
      <ErrorContainer>
        <ErrorIcon>
          <Warning sx={{ fontSize: '48px' }} />
        </ErrorIcon>
        <ErrorMessage>
          {error && error['status'] == 404
            ? t('equipment-has-been-discarded', { ns: 'main-component' })
            : t('equipment-detail-modal-error-title', { ns: 'main-component' })}
        </ErrorMessage>
        <ErrorBriefDescription>
          {error && error['status'] == 404
            ? t('main-component-discarded', { ns: 'main-component' })
            : t('equipment-detail-modal-error-description', { ns: 'main-component' })}
        </ErrorBriefDescription>
        <CloseButtonContainer>
          <CloseButton onClick={pop}>{t('close', { ns: 'common' })}</CloseButton>
        </CloseButtonContainer>
      </ErrorContainer>
    )
  }

  return (
    <GenericDialogLayout
      sx={{ padding: '0px 16px' }}
      renderCustomHeader={pop => (
        <EquipmentHeaderSection
          pop={pop}
          theme={theme}
          data={data}
          certificates={(certificates?.items as ICertificate[]) ?? []}
          customerId={customerId}
          locality={locality}
          disabledAddButton={disabledAddButton}
          context={context}
          isReadOnly={isReadOnlyMode}
          topAction={
            <Flex.Row justifyContent={'center'} alignItems={'center'} gap={1}>
              {customerId && data?.id != null && (
                <FollowButton customerId={customerId} entityType="MainComponent" entityId={data.id} />
              )}
              {/* // TODO: Activity templates feature is unavailable at the moment */}

              {/* <Tooltip title={t('startActivity', { ns: 'interactive-schema' }) as string}>
                <StyledIconButton onClick={handleStartActivity} size="small" disabled={isReadOnlyMode}>
                  <PlayArrow sx={{ fontSize: '20px' }} />
                </StyledIconButton>
              </Tooltip> */}
              {data.type.componentType === IMainComponentType.Net &&
                !(data?.selectedLocalityId && data.status === MainComponentStatus.Outgoing) &&
                isCustomerGlobalServiceStationProvider && (
                  <AuthorizationProvider
                    permissions={[permissionValue.ClientAdminPermission]}
                    Context={context}
                    type={AuthorizationType.Disabled}
                  >
                    <Tooltip title={t('changeOwner', { ns: 'main-component' }) as string}>
                      <StyledIconButton onClick={onChangeEquipmentOwner} size="small">
                        <Person sx={{ fontSize: '20px' }} />
                      </StyledIconButton>
                    </Tooltip>
                  </AuthorizationProvider>
                )}
              <Tooltip title={t('download-deviation-report', { ns: 'main-component' }) as string}>
                <StyledIconButton
                  onClick={async () => {
                    await generateAsync(
                      {
                        customerId: customerId,
                        equipmentId: mainComponentId,
                        reportId: SystemReportTypes.EquipmentReport,
                      },
                      {
                        onSuccess: data => {
                          downloadFileLocal(data, 'equipment-report')
                        },
                      },
                    )
                  }}
                  size="small"
                >
                  {isGenerating ? (
                    <Spinner size={'20px'} />
                  ) : (
                    <DocumentDownload
                      height={'20px'}
                      width={'20px'}
                      color={getThemeColor(theme, MUIThemeColors.primaryMain)}
                    />
                  )}
                </StyledIconButton>
              </Tooltip>
              {data?.positionId != null && data.status === MainComponentStatus.Active && (
                <AuthorizationProvider
                  permissions={[
                    permissionValue.Locality.Mooring.Write,
                    permissionValue.ServiceStation.Write,
                    permissionValue.ClientAdminPermission,
                    permissionValue.ClientAdminPortal.Equipment_Portal,
                  ]}
                  Context={context}
                  type={AuthorizationType.Disabled}
                >
                  <Tooltip title={t('release', { ns: 'main-component' }) as string}>
                    <StyledIconButton onClick={onRelease} size="small" disabled={isReadOnlyMode}>
                      <ReleaseIcon height={'20px'} width={'20px'} />
                    </StyledIconButton>
                  </Tooltip>
                </AuthorizationProvider>
              )}
              {data.status !== MainComponentStatus.NewlyRegistered &&
                ((data?.selectedLocalityId && data.status === MainComponentStatus.InLocalityStock) ||
                  (data?.selectedLocalityId && data.status === MainComponentStatus.Outgoing) ||
                  (data?.positionId && data.status === MainComponentStatus.Active)) && (
                  <AuthorizationProvider
                    permissions={[
                      permissionValue.Locality.Mooring.Write,
                      permissionValue.ServiceStation.Write,
                      permissionValue.ClientAdminPermission,
                      permissionValue.ClientAdminPortal.Equipment_Portal,
                    ]}
                    Context={context}
                    type={AuthorizationType.Disabled}
                  >
                    <Tooltip title={t('change-location', { ns: 'main-component' }) as string}>
                      <StyledIconButton
                        onClick={() => {
                          if (!isLoadingLocality)
                            showChangeLocationDialog(data as IMainComponent, (localityId ?? locality?.id) as EntityId)
                        }}
                        disabled={isLoadingLocality || isReadOnlyMode}
                        size="small"
                      >
                        {isLoadingLocality ? (
                          <Spinner size={'20px'} />
                        ) : (
                          <img height={'20px'} width={'20px'} src="/icons/change.svg" />
                        )}
                      </StyledIconButton>
                    </Tooltip>
                  </AuthorizationProvider>
                )}
              {data.status === MainComponentStatus.NewlyRegistered && (
                <AuthorizationProvider
                  permissions={[
                    permissionValue.Locality.Mooring.Write,
                    permissionValue.ServiceStation.Write,
                    permissionValue.ClientAdminPermission,
                    permissionValue.ClientAdminPortal.Equipment_Portal,
                  ]}
                  Context={context}
                  type={AuthorizationType.Disabled}
                >
                  <Tooltip title={t('sendToLocality', { ns: 'main-component' }) as string}>
                    <StyledIconButton onClick={onSendToLocality} size="small" disabled={isReadOnlyMode}>
                      <LocalityIcon
                        style={{ height: 29, width: 29 }}
                        aria-hidden
                        color={getThemeColor(theme, MUIThemeColors.primaryMain)}
                      />
                    </StyledIconButton>
                  </Tooltip>
                </AuthorizationProvider>
              )}
              {((data?.selectedLocalityId && data.status === MainComponentStatus.InLocalityStock) ||
                (data?.positionId && data.status === MainComponentStatus.Active) ||
                (data?.selectedLocalityId && data.status === MainComponentStatus.Outgoing)) && (
                <AuthorizationProvider
                  permissions={[
                    permissionValue.Locality.Mooring.Write,
                    permissionValue.ServiceStation.Write,
                    permissionValue.ClientAdminPermission,
                    permissionValue.ClientAdminPortal.Equipment_Portal,
                  ]}
                  Context={context}
                  type={AuthorizationType.Disabled}
                >
                  <Tooltip title={t('sendTo', { ns: 'main-component' }) as string}>
                    <StyledIconButton onClick={onSend} size="small" disabled={isReadOnlyMode}>
                      <SendToServiceStationIcon
                        style={{ height: 27, width: 27 }}
                        color={getThemeColor(theme, MUIThemeColors.primaryMain)}
                        aria-hidden
                      />
                    </StyledIconButton>
                  </Tooltip>
                </AuthorizationProvider>
              )}
              <AuthorizationProvider
                permissions={[
                  permissionValue.Locality.Mooring.Write,
                  permissionValue.ServiceStation.Write,
                  permissionValue.ClientAdminPermission,
                  permissionValue.ClientAdminPortal.Equipment_Portal,
                ]}
                Context={context}
                type={AuthorizationType.Disabled}
              >
                <Tooltip title={t('edit', { ns: 'common' }) as string}>
                  <StyledIconButton onClick={() => onUpdate(data.id)} size="small" disabled={isReadOnlyMode}>
                    <Edit sx={{ fontSize: '20px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
                  </StyledIconButton>
                </Tooltip>
              </AuthorizationProvider>
            </Flex.Row>
          }
        />
      )}
      pop={pop}
      actions={[]}
      removedCloseButton={true}
    >
      <>
        <Grid container spacing={0} height={'auto'}>
          <Grid md={12} className="pr-8-core" item xs={12} sm={12}>
            <AuthorizationProvider
              type={AuthorizationType.Disabled}
              Context={context}
              permissions={[
                permissionValue.ServiceStation.Read,
                permissionValue.Locality.MainComponent.Deviation.Read,
                permissionValue.Locality.MainComponent.Deviation.Write,
                permissionValue.Locality.MainComponent.Deviation.Delete,
                permissionValue.ClientAdminPermission,
                permissionValue.ClientAdminPortal.Equipment_Portal,
              ]}
            >
              <Box>
                <DeviationSection
                  customerId={customerId!}
                  mainComponentId={data.id}
                  storageId={storageId}
                  selectedLocalityId={localityId}
                  permission={permission}
                  serviceStationId={serviceStationId}
                  context={context as Context<any>}
                  pop={pop}
                  mainComponent={data}
                  locality={locality}
                  localityId={locality?.id}
                  isReadOnly={isReadOnlyMode}
                  enableDelete={hasPermission(
                    [
                      permissionValue.Locality.MainComponent.Deviation.Delete,
                      permissionValue.ServiceStation.Delete,
                      permissionValue.ClientAdminPermission,
                      permissionValue.ClientAdminPortal.Equipment_Portal,
                    ],
                    permission,
                  )}
                  isLocalityPortal={!!localityId}
                />
              </Box>
            </AuthorizationProvider>
            <DocumentsAndEventsSection
              context={context}
              customerId={customerId}
              localityId={localityId}
              mainComponentId={mainComponentId}
              mainComponent={data}
              isReadOnly={isReadOnlyMode}
              locality={locality}
            />
            <Box mt={2}>
              <WashingReportAndImpregnationSection
                context={context}
                mainComponentId={mainComponentId}
                mainComponent={data}
                serviceStationId={serviceStationId}
                isReadOnly={isReadOnlyMode}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid md={12} className="px-0-core" item xs={12} mt={2} sm={12} sx={{ marginBottom: '16px' }}>
          <ROVSection
            context={context}
            customerId={customerId}
            localityId={localityId}
            mainComponentId={mainComponentId as EntityId}
            mooringId={data?.position?.mooringId}
            rovVideosWithSharedCategories={rovVideosWithSharedCategories}
            selectedCategoryId={selectedCategoryId}
            onCategoryChange={setSelectedCategoryId}
            onAddRovVideo={onAddRovVideo}
            isReadOnly={isReadOnlyMode}
          />
        </Grid>
        <Grid md={12} className="px-0-core" item xs={12} sm={12} sx={{ height: 'fit-content' }}>
          <HistoryAndMaintenanceSection
            context={context}
            customerId={customerId as EntityId}
            localityId={localityId as EntityId}
            mainComponentId={mainComponentId as EntityId}
            mainComponent={data}
            locality={locality}
            pop={pop}
            permission={permission}
          />
        </Grid>
      </>
    </GenericDialogLayout>
  )
}

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  minHeight: '300px',
  textAlign: 'center',
  position: 'relative',
}))

const ErrorIcon = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  color: getThemeColor(theme, MUIThemeColors.errorMain),
}))

const CloseButtonContainer = styled(Box)({
  position: 'absolute',
  bottom: '16px',
  right: '16px',
})

const CloseButton = styled('button')(({ theme }) => ({
  padding: '8px 16px',
  backgroundColor: getThemeColor(theme, MUIThemeColors.errorMain),
  border: 'none',
  color: 'white',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.errorDark),
  },
}))

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.errorMain),
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '8px',
}))

const ErrorBriefDescription = styled(Typography)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  fontSize: '13px',
  fontWeight: 400,
  marginBottom: '24px',
  maxWidth: '400px',
  margin: '0 auto 24px auto',
}))

export const StyledTypography = styled(Typography)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.black),
  fontSize: '24px',
  fontWeight: 600,
}))

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  '&:disabled svg': {
    color: getThemeColor(theme, MUIThemeColors.secondaryDark),
    opacity: 0.5,
  },
  '&:disabled svg path': {
    fill: getThemeColor(theme, MUIThemeColors.secondaryDark),
  },
  '&:disabled img': {
    opacity: 0.5,
  },
}))
